import { Box, Typography } from "@mui/material";
import { Booking, BookingStatus } from "src/business/booking";
import { BookingDateGroup } from "./BookingDateGroup";
import { StatusFilter } from "./BookingSearchBar";
import { colorTextMuted } from "src/design/tokens";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

interface Props {
  bookings: Booking[];
  search: string;
  statusFilter: StatusFilter;
  onStatusChange: (booking: Booking, status: BookingStatus) => void;
  isBookingUpdateEnabled: boolean;
  restaurantName: string;
}

const formatDateLabel = (dateStr: string, todayLabel: string): string => {
  const d = dayjs(dateStr);
  if (d.isSame(dayjs(), "day")) return todayLabel;
  return d.format("dddd, MMMM D, YYYY");
};

export const BookingList = ({
  bookings,
  search,
  statusFilter,
  onStatusChange,
  isBookingUpdateEnabled,
  restaurantName,
}: Props) => {
  const { t } = useTranslation();

  const filtered = bookings.filter((b) => {
    const q = search.toLowerCase();
    const matchSearch =
      !search ||
      b.userName.toLowerCase().includes(q) ||
      b.email.toLowerCase().includes(q) ||
      b.phone.toLowerCase().includes(q);

    const matchStatus =
      statusFilter === "all" || b.bookingStatus === statusFilter;

    return matchSearch && matchStatus;
  });

  if (filtered.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography
          sx={{ fontSize: "15px", fontWeight: 500, color: colorTextMuted }}
        >
          {t("bookings.list.noBookings")}
        </Typography>
        <Typography sx={{ fontSize: "13px", color: colorTextMuted, mt: 0.5 }}>
          {t("bookings.list.noBookingsSubtitle")}
        </Typography>
      </Box>
    );
  }

  const groups: Record<string, Booking[]> = {};
  for (const b of filtered) {
    const key = dayjs(b.bookingDate).format("YYYY-MM-DD");
    if (!groups[key]) groups[key] = [];
    groups[key].push(b);
  }

  const sortedDates = Object.keys(groups).sort((a, b) =>
    dayjs(a).diff(dayjs(b)),
  );
  const todayLabel = t("bookings.filters.today");

  return (
    <Box>
      {sortedDates.map((date) => (
        <BookingDateGroup
          isBookingUpdateEnabled={isBookingUpdateEnabled}
          key={date}
          dateLabel={formatDateLabel(date, todayLabel)}
          bookings={groups[date]}
          onStatusChange={onStatusChange}
          restaurantName={restaurantName}
        />
      ))}
    </Box>
  );
};
